import {
  actionCardStatusLabel,
  isSlackMessage,
  resolveActionCard,
  type ActionCardCallbacks,
  type ActionCardPreviewContent,
  type ActionCardPreviewState,
  type ActionCardPreviousFieldValues,
  type ActionCardProvenance,
  type ActionCardRevision,
  type ActionCardSourceLink,
} from '@littlebird/action-cards';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Box, XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { confirmationIcon } from '../icons';
import { useFieldEditing } from '../use-field-editing';

import { ActionCardContextNote } from './ActionCardContextNote';
import { ActionCardFields } from './ActionCardFields';
import { ActionCardIcon } from './ActionCardIcon';
import { ActionCardSourceLinkAnchor } from './ActionCardSourceLink';
import { ExternalLink } from './ExternalLink';

import type { JSONObject } from '@littlebird/sdk';
import type { KeyboardEvent } from 'react';

type ActionCardRowProps = {
  content: ActionCardPreviewContent;
  state: ActionCardPreviewState;
  expanded: boolean;
  externalUrl?: string;
  externalRef?: JSONObject | null;
  revision?: ActionCardRevision;
  isRevisionEdit?: boolean;
  failureMessage?: string;
  completionResult?: JSONObject | null;
  provenance?: ActionCardProvenance;
  context?: string;
  sourceLink?: ActionCardSourceLink;
  previousFieldValues?: ActionCardPreviousFieldValues;
  fullWidth?: boolean;
  callbacks: ActionCardCallbacks;
  onExpandedChange: (expanded: boolean) => void;
  onContentChange: (content: ActionCardPreviewContent) => void;
};

export const ActionCardRow = ({
  content,
  state,
  expanded,
  externalUrl,
  externalRef,
  revision,
  isRevisionEdit,
  failureMessage,
  completionResult,
  provenance,
  context,
  sourceLink,
  previousFieldValues,
  fullWidth = false,
  callbacks,
  onExpandedChange,
  onContentChange,
}: ActionCardRowProps) => {
  const editing = state === 'editing';
  const resolved = resolveActionCard(content);
  const { kind } = resolved;
  const title = resolved.getActionLabel(externalRef);
  const hasSlackQuote = isSlackMessage(content) && !!content.parent_message;
  const ConfirmationIcon = confirmationIcon[kind.actionType];
  const terminal = state === 'dismissed' || state === 'revoked';
  const canRevise = revision?.canRevise(externalRef ?? null) ?? false;
  const canExpand = !terminal && (state !== 'completed' || canRevise);
  const fieldsOnScreen = expanded && canExpand;
  const canConfirm =
    kind.capability === 'confirmable' && (fieldsOnScreen || !kind.runsOnlyOpen);
  const { rootRef, activateField, autoFocusFieldKey } = useFieldEditing(
    editing,
    callbacks
  );
  const fieldsActivatable =
    state === 'draft' || (state === 'completed' && canRevise);
  const fields = resolved.getFields(externalRef);
  const detail = resolved.getSummary(externalRef);
  const provenanceLabel = [provenance?.sourceLabel, provenance?.timestampLabel]
    .filter(Boolean)
    .join(' · ');
  const expandedProvenance = provenanceLabel
    ? [
        provenance?.sourceLabel ? `From ${provenance.sourceLabel}` : undefined,
        provenance?.timestampLabel,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  let statusAnnouncement = '';
  if (state === 'running') {
    statusAnnouncement = canRevise
      ? (revision?.runningLabel ?? kind.runningLabel)
      : kind.runningLabel;
  }
  if (state === 'completed') statusAnnouncement = kind.completedLabel;
  if (state === 'failed') {
    statusAnnouncement = `Failed. ${failureMessage ?? 'This action could not be completed.'}`;
  }
  if (state === 'dismissed' || state === 'completed_externally')
    statusAnnouncement = actionCardStatusLabel(kind, state);
  if (state === 'revoked') statusAnnouncement = 'Withdrawn by Littlebird';

  const toggleExpanded = () => {
    if (canExpand) onExpandedChange(!expanded);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    toggleExpanded();
  };

  return (
    <YStack
      ref={rootRef}
      className={cn(
        'w-full overflow-hidden rounded-xl border border-border bg-surface',
        !fullWidth && 'max-w-xl'
      )}
    >
      {statusAnnouncement ? (
        <span role="status" className="sr-only">
          {statusAnnouncement}
        </span>
      ) : null}
      <XStack className="min-h-14 items-center gap-2.5 px-3.5 py-2">
        <XStack
          role={canExpand ? 'button' : undefined}
          tabIndex={canExpand ? 0 : undefined}
          aria-expanded={canExpand ? expanded : undefined}
          onClick={toggleExpanded}
          onKeyDown={handleKeyDown}
          className={cn(
            'min-w-0 flex-1 items-center gap-2.5 rounded-md outline-none',
            canExpand &&
              'cursor-pointer focus-visible:ring-2 focus-visible:ring-accent'
          )}
        >
          {!hasSlackQuote || !expanded ? (
            <ActionCardIcon
              content={content}
              provider={kind.provider}
              showPerson={!isSlackMessage(content)}
            />
          ) : null}
          <YStack className="min-w-0 flex-1 gap-1">
            <XStack className="min-w-0 items-baseline gap-1.5">
              <span className="shrink-0 text-sm font-medium text-foreground">
                {title}
              </span>
              {detail && detail !== title ? (
                <span
                  className={cn(
                    'truncate text-sm text-foreground-muted',
                    terminal && 'line-through'
                  )}
                >
                  · {detail}
                </span>
              ) : null}
              {!terminal && provenanceLabel ? (
                <span className="hidden truncate text-xs text-foreground-muted md:inline">
                  · {provenanceLabel}
                </span>
              ) : null}
            </XStack>
          </YStack>
        </XStack>
        <XStack className="shrink-0 items-center justify-end gap-1.5">
          {state === 'editing' ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={callbacks.onCancelEdit}
              >
                Cancel
              </Button>
              {isRevisionEdit && revision ? (
                <Button
                  variant="default"
                  size="sm"
                  onClick={callbacks.onConfirm}
                >
                  {revision.confirmationLabel}
                </Button>
              ) : canConfirm ? (
                <Button
                  variant="default"
                  size="sm"
                  iconSize="xs"
                  onClick={callbacks.onConfirm}
                >
                  {ConfirmationIcon ? (
                    <ConfirmationIcon aria-hidden="true" />
                  ) : null}
                  {kind.confirmationLabel}
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  onClick={callbacks.onSaveEdit}
                >
                  Done
                </Button>
              )}
            </>
          ) : state === 'draft' ? (
            <>
              <Button variant="outline" size="sm" onClick={callbacks.onDismiss}>
                Dismiss
              </Button>
              {canConfirm ? (
                <Button
                  variant="default"
                  size="sm"
                  iconSize="xs"
                  onClick={callbacks.onConfirm}
                >
                  {ConfirmationIcon ? (
                    <ConfirmationIcon aria-hidden="true" />
                  ) : null}
                  {kind.confirmationLabel}
                </Button>
              ) : null}
            </>
          ) : state === 'running' ? (
            <span className="text-xs text-foreground-muted">
              {canRevise
                ? (revision?.runningLabel ?? kind.runningLabel)
                : kind.runningLabel}
            </span>
          ) : state === 'completed' ? (
            <>
              <span className="inline-flex items-center gap-1 text-sm font-medium text-success-11">
                <Icons.check className="size-4" aria-hidden="true" />
                {kind.completedLabel}
              </span>
              {state === 'completed' && externalUrl ? (
                <ExternalLink
                  href={externalUrl}
                  className="hidden text-sm text-foreground lg:inline"
                >
                  {kind.getCompletion(externalUrl, completionResult).linkLabel}
                </ExternalLink>
              ) : null}
            </>
          ) : state === 'failed' ? (
            <>
              <span className="text-sm font-medium text-danger-11">Failed</span>
              <span className="hidden truncate text-xs text-danger-11 lg:block">
                {failureMessage ?? 'This action could not be completed.'}
              </span>
              <Button variant="outline" size="sm" onClick={callbacks.onDismiss}>
                Dismiss
              </Button>
              {canConfirm ? (
                <Button variant="default" size="sm" onClick={callbacks.onRetry}>
                  Retry
                </Button>
              ) : null}
            </>
          ) : state === 'dismissed' || state === 'completed_externally' ? (
            <>
              <span className="text-sm text-foreground-muted">
                {actionCardStatusLabel(kind, state)}
              </span>
              {state === 'dismissed' && callbacks.onUndoDismissal ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={callbacks.onUndoDismissal}
                >
                  Undo
                </Button>
              ) : null}
            </>
          ) : (
            <span className="text-sm text-foreground-muted">
              Withdrawn by Littlebird
            </span>
          )}
        </XStack>

        {canExpand ? (
          <button
            type="button"
            aria-label={expanded ? 'Hide details' : 'Show details'}
            aria-expanded={expanded}
            onClick={toggleExpanded}
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-foreground-muted outline-none hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Icons.chevronDown
              className={cn(
                'size-4 transition-transform duration-[220ms] ease-out',
                expanded && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </button>
        ) : null}
      </XStack>

      <Box
        aria-hidden={!expanded || !canExpand}
        inert={!expanded || !canExpand ? true : undefined}
        className={cn(
          'grid transition-[grid-template-rows] duration-[220ms] ease-out motion-reduce:transition-none',
          expanded && canExpand ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <YStack className="min-h-0 overflow-hidden">
          <YStack className="gap-3 border-t border-border-subtle px-3.5 pb-3.5 pt-3">
            {context || resolved.getQuotedMessage() ? (
              <ActionCardContextNote
                context={context}
                quotedMessage={resolved.getQuotedMessage()}
              />
            ) : null}
            <ActionCardFields
              fields={fields}
              attachments={resolved.getAttachments()}
              editing={editing}
              compact
              previousFieldValues={previousFieldValues}
              onFieldChange={(fieldKey, value) =>
                onContentChange(resolved.withUpdatedField(fieldKey, value))
              }
              onFieldActivate={fieldsActivatable ? activateField : undefined}
              autoFocusFieldKey={autoFocusFieldKey}
            />
            {state === 'failed' ? (
              <span className="text-sm text-danger-11">
                {failureMessage ?? 'This action could not be completed.'}
              </span>
            ) : null}
            {expandedProvenance ? (
              <span className="truncate text-xs text-foreground-muted">
                {expandedProvenance}
              </span>
            ) : null}
          </YStack>
        </YStack>
      </Box>
      {sourceLink ? (
        <XStack className="items-center border-t border-border-subtle px-3.5 py-2">
          <ActionCardSourceLinkAnchor sourceLink={sourceLink} />
        </XStack>
      ) : null}
    </YStack>
  );
};
